import React, { Fragment } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { brand, palette } from 'theme';
import Typography from '@material-ui/core/Typography';
import loadable from '@loadable/component';
import BlockContentTypography from './elements/BlockContentTypography';
import BlockContentSection from './elements/BlockContentSection';
import { SectionWrapper } from 'components/Section';

const InlineResourceCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineResourceCTA
});
const InlineCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineCTA
});
const InlineBlogCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineBlogCTA
});
const PhotoCTA = loadable(() => import('components/CTACards'), {
  resolveComponent: components => components.PhotoCTA
});
const Figure = loadable(() => import('components/Figure'));
const FigureGallery = loadable(() => import('components/FigureGallery'));
const Container = loadable(() => import('components/Container'));
const PaddedDivider = loadable(() => import('components/PaddedDivider'));
const VideoPlayer = loadable(() => import('components/VideoPlayer'));
const PullQuote = loadable(() => import('components/PullQuote'));
const Quote = loadable(() => import('components/Quote'));
const TableauReport = loadable(() => import('components/TableauReport'));
const PublicationCard = loadable(() =>
  import('views/Publications/elements/PublicationCard')
);
const PodcastCard = loadable(() =>
  import('views/Podcast/elements/PodcastCard')
);
const CaseStudyCard = loadable(() =>
  import('views/CaseStudies/elements/CaseStudyCard')
);

const Table = loadable(() => import('components/Table'));
const TextGrid = loadable(() => import('components/TextGrid'));
const StatList = loadable(() => import('components/StatList'));
const TabbedText = loadable(
  () => import('views/Applications/Detail/elements/ApplicationSection'),
  {
    resolveComponent: components => components.ApplicationSubsections
  }
);
const Accordion = loadable(() => import('components/Accordion'));
const IconRow = loadable(() => import('components/IconRow'));

const RelatedPublications = loadable(() =>
  import('components/RelatedPublications')
);

const TimelineContainer = loadable(() =>
  import('./elements/TimelineContainer')
);

const BlockContainer = styled.div`
  padding-bottom: 18px;
`;

const ContainerWrapper = ({ variant, children }) => {
  if (variant === 'bold') {
    return <Container>{children}</Container>;
  } else if (variant === 'timeline') {
    return <TimelineContainer>{children}</TimelineContainer>;
  } else {
    return children;
  }
};

const FullWidthBackground = styled.div`
  background: ${props =>
    props.backgroundGradient
      ? `linear-gradient(${props.backgroundGradient})`
      : props.backgroundColor};
`;

const BlockContent = ({ variant, sections }) => {
  let sectionNumber = 0;
  const sectionCount = _.filter(sections, 'section').length;
  return (
    <span data-swiftype-name="body" data-swiftype-type="text">
      {(sections || []).map((block, idx) => {
        if (block.section) sectionNumber++;

        const selectedBrandColor =
          brand?.[block.section?.background?.brand_color];
        const selectedTheme =
          selectedBrandColor?.contrastTheme || block.section?.background?.theme;
        const selectedColor = block.section?.background?.color;

        const WrapperComponent = variant === 'simple' ? Container : Fragment;

        return (
          <Fragment key={idx}>
            {block.section && (
              <FullWidthBackground
                backgroundGradient={selectedBrandColor?.gradient?.primary}
                backgroundColor={selectedColor}
              >
                <WrapperComponent>
                  <BlockContentSection
                    section={block.section}
                    variant={variant}
                    sectionNumber={sectionNumber}
                    sectionCount={sectionCount}
                    overrideTheme={selectedTheme}
                    idx={idx}
                  >
                    {block.section.content.map((block, idx) => (
                      <Fragment key={idx}>
                        {block.rich_text && (
                          <BlockContentTypography
                            variant="body2"
                            dangerouslySetInnerHTML={{
                              __html: block.rich_text.text
                            }}
                            {...block.rich_text?.$?.text}
                          />
                        )}

                        {block.heading && (
                          <>
                            {block.heading.variant === 'overline' ? (
                              <Typography
                                variant="overline"
                                gutterBottom
                                {...block.heading?.$?.text}
                              >
                                {block.heading.text}
                              </Typography>
                            ) : (
                              <BlockContentTypography
                                variant="body2"
                                dangerouslySetInnerHTML={{
                                  __html: `<${block.heading.variant}>${block.heading.text}</${block.heading.variant}>`
                                }}
                                {...block.heading?.$?.text}
                              />
                            )}
                          </>
                        )}

                        {block.pull_quote && (
                          <PullQuote
                            {...block.pull_quote}
                            {...block?.$?.pull_quote}
                          />
                        )}

                        {block.divider && (
                          <PaddedDivider
                            hide={block.divider?.visibility === 'hidden'}
                            {...block?.$?.divider}
                          />
                        )}

                        {block.quote && (
                          <BlockContainer {...block?.$?.quote}>
                            <Quote
                              quote={_.get(block.quote, 'quote_reference[0]')}
                              size="small"
                            />
                          </BlockContainer>
                        )}

                        {block.image && (
                          <Figure
                            {...block.image}
                            maxLayoutWidth={block.image?.max_width}
                            {...block?.$?.image}
                          />
                        )}

                        {block.figure && (
                          <Figure
                            {..._.get(block.figure, 'figure_reference[0]')}
                            align={block.figure?.align}
                            maxLayoutWidth={block.figure?.max_width}
                            {...block?.$?.figure}
                          />
                        )}

                        {block.figure_gallery && (
                          <>
                            <FigureGallery
                              {...block?.figure_gallery}
                              {...block?.$?.figure_gallery}
                            />
                          </>
                        )}

                        {block.tableau && (
                          <TableauReport
                            {...block.tableau}
                            {...block?.$?.tableau}
                          />
                        )}

                        {block.video && (
                          <BlockContainer>
                            <VideoPlayer
                              {...block.video}
                              {...block?.$?.video}
                            />
                          </BlockContainer>
                        )}

                        {block.publication && (
                          <BlockContainer {...block?.$?.publication}>
                            <PublicationCard
                              publication={_.get(
                                block.publication,
                                'publication_reference[0]'
                              )}
                              elevation={0}
                              showBackground
                            />
                          </BlockContainer>
                        )}

                        {block.table && (
                          <BlockContainer {...block?.$?.table}>
                            <Table
                              {..._.get(
                                block.table,
                                'table_reference[0].table'
                              )}
                            />
                          </BlockContainer>
                        )}

                        {block.text_grid && (
                          <BlockContainer {...block?.$?.text_grid}>
                            <TextGrid {...block.text_grid} />{' '}
                          </BlockContainer>
                        )}

                        {block.stats && (
                          <BlockContainer {...block?.$?.stats}>
                            <StatList {...block.stats} />
                          </BlockContainer>
                        )}

                        {block.tabbed_text && (
                          <TabbedText subsections={block.tabbed_text?.tabs} />
                        )}

                        {block.accordion && (
                          <BlockContainer {...block?.$?.accordion}>
                            <Accordion {...block.accordion} />
                          </BlockContainer>
                        )}

                        {block.icon_row && (
                          <BlockContainer {...block?.$?.icon_row}>
                            <IconRow {...block.icon_row} />
                          </BlockContainer>
                        )}
                      </Fragment>
                    ))}
                  </BlockContentSection>
                </WrapperComponent>
              </FullWidthBackground>
            )}

            <WrapperComponent>
              {block.pull_quote && (
                <>
                  {variant === 'bold' ? (
                    <SectionWrapper backgroundcolor={palette.grey[50]}>
                      <Container>
                        <PullQuote
                          muted
                          size="large"
                          {...block.pull_quote}
                          {...block?.$?.pull_quote}
                        />
                      </Container>
                    </SectionWrapper>
                  ) : (
                    <PullQuote
                      muted
                      size="large"
                      {...block.pull_quote}
                      {...block?.$?.pull_quote}
                    />
                  )}
                </>
              )}

              <ContainerWrapper variant={variant}>
                {block.resource && (
                  <aside data-swiftype-index="false" {...block?.$?.resource}>
                    <InlineResourceCTA
                      resource={_.get(block.resource, 'resource_reference[0]')}
                    />
                  </aside>
                )}

                {block.blog && (
                  <aside data-swiftype-index="false" {...block?.$?.blog}>
                    <InlineBlogCTA
                      post={_.get(block.blog, 'blog_reference[0]')}
                    />
                  </aside>
                )}

                {block.podcast && (
                  <aside data-swiftype-index="false" {...block?.$?.podcast}>
                    <PodcastCard
                      episode={_.get(block.podcast, 'podcast_reference[0]')}
                      showPlayer={block.podcast?.show_player}
                    />
                  </aside>
                )}

                {block.case_study && (
                  <aside data-swiftype-index="false" {...block?.$?.case_study}>
                    <CaseStudyCard
                      {..._.get(block.case_study, 'case_study_reference[0]')}
                      size={block.case_study?.size}
                    />
                  </aside>
                )}

                {block.cta && (
                  <aside data-swiftype-index="false" {...block?.$?.cta}>
                    {_.get(block.cta, 'cta_reference[0]._content_type_uid') ===
                      'card_cta' && (
                      <InlineCTA
                        size="wide"
                        {..._.get(block.cta, 'cta_reference[0]')}
                      />
                    )}

                    {_.get(block.cta, 'cta_reference[0]._content_type_uid') ===
                      'photo_cta' && (
                      <PhotoCTA {..._.get(block.cta, 'cta_reference[0]')} />
                    )}
                  </aside>
                )}

                {block.related_publications && (
                  <aside
                    data-swiftype-index="false"
                    {...block?.$?.related_publications}
                  >
                    <RelatedPublications
                      researchArea={
                        block.related_publications?.filter_to?.research_area
                      }
                      application={
                        block.related_publications?.filter_to?.application
                      }
                      product={block.related_publications?.filter_to?.product}
                      showControls
                    />
                  </aside>
                )}
              </ContainerWrapper>
            </WrapperComponent>
          </Fragment>
        );
      })}
    </span>
  );
};

export default BlockContent;
